import React, { useRef, useEffect, useState } from 'react'
import styled from 'styled'

import { FooterEditEntity } from 'components/ui/footer'
import { IconPlus } from 'components/ui/icons'
import { Button, ButtonGroup } from 'components/ui/button'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { SectionHeader } from 'components/ui/section-header'
import { StatusGuard } from 'components/ui/status-guard'
import { StatusSummary } from 'components/ui/status-summary'

import { connect } from 'containers/store'
import { sharedActionCreators, SharedActionCreators } from 'containers/shared'

import { GetMoodRequest } from 'generated/mos/org/getty/audioaccess'
import { UpdateMoodRequest } from 'generated/mos/org/getty/audiomanagement'
import { EventPointOfInterest, GenericPointOfInterest, StopPointOfInterest, Mood } from 'generated/mos/org/getty/audio'
import { ValidationFailure } from 'generated/mos/org/getty/validation'
import { ListValidationFailuresRequest } from 'generated/mos/org/getty/validationaccess'
import { releaseControlsBindableActionCreators } from 'domains/release-controls/actions'
import { ReleaseControlUpdate } from 'domains/release-controls/components/release-control-update'
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'
import { Page } from 'generated/mos/pagination'
import { SpacesStore } from 'domains/spaces/store'
import { spacesBindableActionCreators } from 'domains/spaces/actions'

import { space } from 'helpers/style'
import { isNotNullOrUndefined, pick, pluralise } from 'helpers/core'
import { Status, statusSelector } from 'helpers/status'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { getMoodSubnav } from './subnav'
import { PointsOfInterestList } from './points-of-interest-list'
import { MoodDelete } from '../mood-delete'
import { PointOfInterestCreate } from '../../points-of-interest/point-of-interest-create'
import { GenericPointOfInterestCreate } from '../../generic-points-of-interest/generic-point-of-interest-create'
import { EventPointOfInterestCreate } from '../../event-points-of-interest/event-point-of-interest-create'
import { moodSchema } from '../../../schemas/mood'
import { actionCreators, GettyActionCreators } from '../../../actions'
import { GettyStore } from '../../../store'

const FilterButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: -${space(6)};
  margin-bottom: ${space(3)};
`;

const NoResults = styled.div`
  text-align: center;
  padding-top: ${space(5)};
  padding-bottom: ${space(5)};
`;

type DirectProps = {
  projectId: string;
  moodId: string;
  historyPush: (to: string) => void;
};

type ConnectedProps = Pick<GettyStore['getty'], 'moodUpdate' | 'validationFailuresList' | 'sites'> & Pick<SpacesStore["spaces"], "spacesList">

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetMoodRequest' | 'gettyUpdateMoodRequest' | 'gettyListSitesRequest' | 'gettyListValidationFailuresRequest'> &
  Pick<typeof releaseControlsBindableActionCreators, "releaseControlsListReleaseControlsRequest" | "releaseControlsSetReleaseStatusRequest"> &
  Pick<typeof spacesBindableActionCreators, "spacesRequest"> &
  Pick<SharedActionCreators, 'toastNotification'>;

type Props = ActionProps & DirectProps & ConnectedProps;

const statusSummary = (failures: {issues: ReadonlyArray<ValidationFailure.Issue.Entity>} | undefined) => {
  const warning: Array<{text: string}> = [];
  const failureFields = failures && failures.issues.filter(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'MISSING_REQUIRED_ATTRIBUTE')
  if (failureFields && !!failureFields.length) {
    warning.push({ text: `The following fields are required to publish this mood journey: ${failureFields.map(({ attributeDisplayName }) => attributeDisplayName).join(', ') }.` });
  }
  if (failures && failures.issues.find(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'INVALID_COLOR')) {
    warning.push({ text: "Mood journey contains invalid colors" });
  }

  if (warning.length) {
    return function renderStatusSummary() {
      return <StatusSummary warning={warning} />
    }
  } else {
    return undefined
  }
}

const MoodUpdateView = ({
  gettyGetMoodRequest,
  gettyUpdateMoodRequest,
  historyPush,
  projectId,
  toastNotification,
  moodId,
  moodUpdate,
  validationFailuresList,
  gettyListValidationFailuresRequest,
  releaseControlsListReleaseControlsRequest,
  releaseControlsSetReleaseStatusRequest,
  spacesList,
  spacesRequest,
  gettyListSitesRequest,
  sites,
}: Props) => {
  const moodData = statusSelector.data(moodUpdate);
  const mood = moodData && moodData.mood;
  const validationFailuresListData = statusSelector.data(validationFailuresList)
  const spacesListData = statusSelector.data(spacesList)
  const sitesData = statusSelector.data(sites)
  const pointsOfInterest = mood && mood.stopPointsOfInterest ? mood.stopPointsOfInterest : [];
  const genericPointsOfInterest = mood && mood.genericPointsOfInterest ? mood.genericPointsOfInterest : [];
  const eventPointsOfInterest = mood && mood.eventPointsOfInterest ? mood.eventPointsOfInterest : [];

  const moodRef = { typename: Mood.refName, id: moodId };

  const [modalOpen, setModalOpen] = useState(false);
  const [genericModalOpen, setGenericModalOpen] = useState(false);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [deleteMoodModalOpen, setDeleteMoodModalOpen] = useState(false);
  const [newlyCreatedPointOfInterestRef, setNewlyCreatedPointOfInterestRef] = useState<StopPointOfInterest.Entity['ref'] | GenericPointOfInterest.Entity['ref'] | EventPointOfInterest.Entity['ref']>();

  const hasSubmitted = useRef(false)

  useEffect(() => {
    gettyGetMoodRequest({
      type: GetMoodRequest.refName,
      moodRef: { typename: Mood.refName, id: moodId },
    })
    releaseControlsListReleaseControlsRequest([{
      typename: Mood.refName,
      id: moodId
    }])
    gettyListSitesRequest()
    spacesRequest()
    gettyListValidationFailuresRequest({
      ...ListValidationFailuresRequest.defaults,
      page: {
        ...Page.defaults,
        pageSize: 100,
      },
      filter: {
        ...ListValidationFailuresRequest.ByRef.defaults,
        entityRefs: [{
          typename: Mood.refName,
          id: moodId,
        }]
      }
    })
  }, [gettyGetMoodRequest, moodId, spacesRequest, gettyListSitesRequest, releaseControlsListReleaseControlsRequest, gettyListValidationFailuresRequest]);
  // }}}

  const [pois, setPois] = useState<ReadonlyArray<StopPointOfInterest.Entity | GenericPointOfInterest.Entity | EventPointOfInterest.Entity>>([]);

  const mergeList = (
    oldList: ReadonlyArray<StopPointOfInterest.Entity | GenericPointOfInterest.Entity | EventPointOfInterest.Entity>,
    partialNewList: ReadonlyArray<StopPointOfInterest.Entity | GenericPointOfInterest.Entity | EventPointOfInterest.Entity>
  ):  ReadonlyArray<StopPointOfInterest.Entity | GenericPointOfInterest.Entity | EventPointOfInterest.Entity> => {
    if (partialNewList.length === 0) {
      return oldList
    }
    return oldList.filter((x) => x.type !== partialNewList[0].type).concat(partialNewList).sort((x, y) => x.unifiedSortKey - y.unifiedSortKey)
  }

  useEffect(() => {
    if (moodUpdate.status === Status.Ready && mood && Mood.mustRef(mood.ref).id === moodId) {
      setPois(mergeList(mergeList(mergeList(Array.from(pois), pointsOfInterest), genericPointsOfInterest), eventPointsOfInterest))
    }

    if (moodUpdate.status === Status.Ready && hasSubmitted.current) {
      releaseControlsListReleaseControlsRequest([{
        typename: Mood.refName,
        id: moodId
      }])
      toastNotification({ type: 'success', text: 'Mood updated' })
      hasSubmitted.current = false
      gettyListValidationFailuresRequest({
        ...ListValidationFailuresRequest.defaults,
        page: {
          ...Page.defaults,
          pageSize: 100,
        },
        filter: {
          ...ListValidationFailuresRequest.ByRef.defaults,
          entityRefs: [{
            typename: Mood.refName,
            id: moodId,
          }]
        }
      })
    }
  }, [moodUpdate.status]) // eslint-disable-line react-hooks/exhaustive-deps
  // }}}

  const name = mood && mood.name ? moodSchema.name.read(mood.name) : '';

  const onPointOfInterestCreate = (pointOfInterest: StopPointOfInterest.Entity) => {
    releaseControlsListReleaseControlsRequest([{
      typename: Mood.refName,
      id: moodId
    }])
    setNewlyCreatedPointOfInterestRef(pointOfInterest.ref);
    setModalOpen(false);
    toastNotification({ type: 'success', text: '1 new point of interest added', durationMs: 2000 });
    gettyGetMoodRequest({
      type: GetMoodRequest.refName,
      moodRef: { typename: Mood.refName, id: moodId },
    });
    gettyListValidationFailuresRequest({
      ...ListValidationFailuresRequest.defaults,
      page: {
        ...Page.defaults,
        pageSize: 100,
      },
      filter: {
        ...ListValidationFailuresRequest.ByRef.defaults,
        entityRefs: [{
          typename: Mood.refName,
          id: moodId,
        }]
      }
    })
  }

  const onGenericPointOfInterestCreate = (genericPointOfInterest: GenericPointOfInterest.Entity) => {
    releaseControlsListReleaseControlsRequest([{
      typename: Mood.refName,
      id: moodId
    }])
    setNewlyCreatedPointOfInterestRef(genericPointOfInterest.ref);
    setGenericModalOpen(false);
    toastNotification({ type: 'success', text: '1 new generic point of interest added', durationMs: 2000 });
    gettyGetMoodRequest({
      type: GetMoodRequest.refName,
      moodRef: { typename: Mood.refName, id: moodId },
    });
    gettyListValidationFailuresRequest({
      ...ListValidationFailuresRequest.defaults,
      page: {
        ...Page.defaults,
        pageSize: 100,
      },
      filter: {
        ...ListValidationFailuresRequest.ByRef.defaults,
        entityRefs: [{
          typename: Mood.refName,
          id: moodId,
        }]
      }
    })
  }

  const onEventPointOfInterestCreate = (eventPointOfInterest: EventPointOfInterest.Entity) => {
    releaseControlsListReleaseControlsRequest([{
      typename: Mood.refName,
      id: moodId
    }])
    setNewlyCreatedPointOfInterestRef(eventPointOfInterest.ref);
    setEventModalOpen(false);
    toastNotification({ type: 'success', text: '1 new event point of interest added', durationMs: 2000 });
    gettyGetMoodRequest({
      type: GetMoodRequest.refName,
      moodRef: { typename: Mood.refName, id: moodId },
    });
    gettyListValidationFailuresRequest({
      ...ListValidationFailuresRequest.defaults,
      page: {
        ...Page.defaults,
        pageSize: 100,
      },
      filter: {
        ...ListValidationFailuresRequest.ByRef.defaults,
        entityRefs: [{
          typename: Mood.refName,
          id: moodId,
        }]
      }
    })
  }

  const updateMood = () => {
    const stopPointOfInterestRefs = pois
      .filter(({type}) => type == 'mos.org.getty.audio.StopPointOfInterest')
      .map(stopPointOfInterest => stopPointOfInterest.ref)
      .filter(isNotNullOrUndefined) as ReadonlyArray<{ readonly typename: "mos.org.getty.audio.StopPointOfInterest"; readonly id: string }>
    const eventPointOfInterestRefs = pois
      .filter(({type}) => type == 'mos.org.getty.audio.EventPointOfInterest')
      .map(stopPointOfInterest => stopPointOfInterest.ref)
      .filter(isNotNullOrUndefined) as ReadonlyArray<{ readonly typename: "mos.org.getty.audio.EventPointOfInterest"; readonly id: string }>
    const genericPointOfInterestRefs = pois
      .filter(({type}) => type == 'mos.org.getty.audio.GenericPointOfInterest')
      .map(stopPointOfInterest => stopPointOfInterest.ref)
      .filter(isNotNullOrUndefined) as ReadonlyArray<{ readonly typename: "mos.org.getty.audio.GenericPointOfInterest"; readonly id: string }>
    const sortedPointOfInterestRefs = pois
      .map(stopPointOfInterest => stopPointOfInterest.ref)
      .filter(isNotNullOrUndefined) as ReadonlyArray<{ 
        readonly typename: "mos.org.getty.audio.GenericPointOfInterest" | "mos.org.getty.audio.StopPointOfInterest" | "mos.org.getty.audio.EventPointOfInterest"; 
        readonly id: string;
      }>

    gettyUpdateMoodRequest({
      ...UpdateMoodRequest.defaults,
      updateMask: {
        type: "google.protobuf.FieldMask",
        fields: ['stopPointOfInterestRefs', 'genericPointOfInterestRefs', 'eventPointOfInterestRefs', 'sortedPointOfInterestRefs'],
      },
      moodRef,
      stopPointOfInterestRefs,
      genericPointOfInterestRefs,
      eventPointOfInterestRefs,
      sortedPointOfInterestRefs,
    });

    hasSubmitted.current = true
  }

  const modified = !mergeList(mergeList(mergeList(Array.from(pois), pointsOfInterest), genericPointsOfInterest), eventPointsOfInterest).every((pointOfInterest, i) => {
    const orderedPointOfInterest = pois[i];
    return orderedPointOfInterest && orderedPointOfInterest.ref && pointOfInterest.ref && orderedPointOfInterest.ref.id === pointOfInterest.ref.id;
  })  // relies on mood update to do deletion
  const deletePointOfInterestFromMood = (pointOfInterestRef: StopPointOfInterest.Ref | EventPointOfInterest.Ref | GenericPointOfInterest.Ref) => {
    const pointsOfInterest = pois.filter(pointOfInterest => pointOfInterest.ref !== pointOfInterestRef)
    setPois(pointsOfInterest);
  }

  const releaseControlUpdate = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => {
    mood && releaseControlsSetReleaseStatusRequest({
      ...SetReleaseStatusRequest.defaults,
      ref: moodRef,
      bundles,
    })
  }

  const failures = validationFailuresListData ? validationFailuresListData.find(({ ref }) => ref && ref.id === moodId) : undefined
  const failureFields = failures && failures.issues.filter(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'MISSING_REQUIRED_ATTRIBUTE' || issueType === 'TOUR_CONTAINS_STOPS_AND_HAS_WEBPAGE_URL')

  return (
    <>
      {modalOpen && (
        <PointOfInterestCreate
          onCancel={() => setModalOpen(false)}
          moodRef={moodRef}
          onComplete={onPointOfInterestCreate}
          projectId={projectId}
          historyPush={historyPush}
        />
      )}
      {genericModalOpen && (
        <GenericPointOfInterestCreate
          onCancel={() => setGenericModalOpen(false)}
          moodRef={moodRef}
          onComplete={onGenericPointOfInterestCreate}
          projectId={projectId}
          historyPush={historyPush}
        />
      )}
      {eventModalOpen && (
        <EventPointOfInterestCreate
          onCancel={() => setEventModalOpen(false)}
          moodRef={moodRef}
          onComplete={onEventPointOfInterestCreate}
          projectId={projectId}
          historyPush={historyPush}
        />
      )}
      {deleteMoodModalOpen && (
        <MoodDelete
          onCancel={() => setDeleteMoodModalOpen(false)}
          moodRef={moodRef}
          projectId={projectId}
          historyPush={historyPush}
        />
      )}
      <LayoutListOrFormPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title="Edit Mood Journey"
            onConfirm={modified ? () => historyPush(`/project/${projectId}/moods`) : undefined}
            navBackRoute={`/project/${projectId}/moods`}
          />
        )}
        title={typeof name === 'string' ? name : ''}
        toolbar={() => (
          <ButtonGroup>
            {mood &&
              <ReleaseControlUpdate
                hasBorder
                entityRef={moodRef}
                updateHandler={releaseControlUpdate}
              />
            }
            <PopOverMenu hasBorder={true}>
              <PopOverMenuButton onClick={() => setDeleteMoodModalOpen(true)}>
                Delete
              </PopOverMenuButton>
            </PopOverMenu>
          </ButtonGroup>
        )}
        subnav={() => (
          <SecondaryNavigation navItems={getMoodSubnav(projectId, moodId)} modified={modified} historyPush={historyPush} >
            <Button variant="subtle" onClick={() => setModalOpen(true)}><IconPlus size="small" />
              <strong> create point of interest</strong>
            </Button>
            <Button variant="subtle" onClick={() => setGenericModalOpen(true)}><IconPlus size="small" />
              <strong> create generic point of interest</strong>
            </Button>
            <Button variant="subtle" onClick={() => setEventModalOpen(true)}><IconPlus size="small" />
              <strong> create event point of interest</strong>
            </Button>
          </SecondaryNavigation>
        )}
        summary={statusSummary(failures)}
        content={() => (
          <>
            <StatusGuard status={moodUpdate}>
              <SectionHeader heading={`${pois.length} ${pluralise(pois.length, "point of interest", "points of interest")}`} />
              {!pois.length ? (
                <NoResults>
                  <h3>No Points of Interest</h3>
                  <p>This tour has no points of interest. Add or create some points of interest.</p>
                </NoResults>
              ) : (
                <PointsOfInterestList
                  historyPush={historyPush}
                  projectId={projectId}
                  pointsOfInterest={pois}
                  setOrderedPointsOfInterest={setPois}
                  moodId={moodId}
                  newlyCreatedPointOfInterestRef={newlyCreatedPointOfInterestRef}
                  deleteFromMood={deletePointOfInterestFromMood}
                  spacesListData={spacesListData}
                  sitesData={sitesData && sitesData.sites}
                />
              )}
            </StatusGuard>
          </>
        )}
        footer={() => (
          <FooterEditEntity
            entity={moodUpdate}
            modified={modified}
            onCancel={() => historyPush(`/project/${projectId}/moods`)}
            onSave={updateMood}
          />
        )}
      />
    </>
  )
}

export const MoodUpdate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => ({
    ...pick(store.getty, 'moodUpdate', 'sites', 'validationFailuresList'),
    ...pick(store.spaces, "spacesList"),
  }),
  {
    ...pick(actionCreators, 'gettyGetMoodRequest', 'gettyUpdateMoodRequest', 'gettyListValidationFailuresRequest', 'gettyListSitesRequest',),
    ...pick(releaseControlsBindableActionCreators, 'releaseControlsListReleaseControlsRequest', 'releaseControlsSetReleaseStatusRequest'),
    ...pick(spacesBindableActionCreators, "spacesRequest"),
    ...pick(sharedActionCreators, 'toastNotification'),
  },
)(MoodUpdateView);
